import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { gql, useQuery } from '@apollo/client';
import { Alert, Autocomplete, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import { SelectOption } from 'global';

const QUERY_VENDOR_PROFILES_ASSOCIATED_TO_LENDER = gql`
  query vendorProfilesByDynamicsLenderAccountId($dynamicsAccountId: ID!) {
    vendorProfilesByDynamicsLenderAccountId(dynamicsAccountId: $dynamicsAccountId) {
      id
      dynamicsId
      name
    }
  }
`;

interface VendorProfileOption {
  id: string;
  dynamicsId: string;
  name: string;
}

interface Props {
  value: string;
  onChange: (dynamicsId: string) => void;
}

// Shown when review_reasons includes DEALER_NOT_MATCHED — the workflow couldn't confidently
// resolve the dealer named in the document, so a human has to pick one before this can be confirmed.
export const DealerResolutionField = ({ value, onChange }: Props) => {
  const account = useSelector((state: any) => state.account);

  const { data, loading, error } = useQuery<{ vendorProfilesByDynamicsLenderAccountId: VendorProfileOption[] }>(
    QUERY_VENDOR_PROFILES_ASSOCIATED_TO_LENDER,
    {
      skip: !account?.dynamicsAccountId,
      variables: { dynamicsAccountId: account?.dynamicsAccountId },
    }
  );

  const dealerOptions: SelectOption[] = useMemo(
    () =>
      (data?.vendorProfilesByDynamicsLenderAccountId ?? []).map(vp => ({
        label: vp.name.replace('VP - ', ''),
        value: vp.dynamicsId,
      })),
    [data]
  );

  const selectedOption = dealerOptions.find(option => option.value === value) ?? null;

  return (
    <Alert severity="warning" icon={false} sx={{ '& .MuiAlert-message': { overflow: 'visible', width: '100%' } }}>
      <Stack spacing={1}>
        <Typography variant="body2">
          We couldn't confidently match this application to one of your connected dealers. Select the correct dealer
          before confirming.
        </Typography>
        {error ? (
          <Typography variant="body2" color="error">
            Couldn't load your connected dealers. Please refresh the page and try again.
          </Typography>
        ) : !loading && dealerOptions.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No connected dealers were found for your account.
          </Typography>
        ) : (
          <Autocomplete
            fullWidth
            size="small"
            loading={loading}
            options={dealerOptions}
            value={selectedOption}
            disabled={loading}
            isOptionEqualToValue={(option, val) => option.value === val.value}
            onChange={(_, newValue) => onChange((newValue as SelectOption | null)?.value ?? '')}
            renderInput={params => (
              <TextField
                {...params}
                label="Dealer"
                variant="standard"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading ? <CircularProgress color="inherit" size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        )}
      </Stack>
    </Alert>
  );
};
